import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCampaignsQuery } from "./campaigns/useCampaignsQuery";
import { useCampaignMutations } from "./campaigns/useCampaignMutations";
import { Campaign } from "@/types/campaigns";
import { CampaignFormData } from "@/components/campaigns/modal/CampaignFormSchema";
import { Campaign as LegacyCampaign } from "@/components/campaigns/CampaignModal";
import { toast } from "sonner";

export const useCampaigns = () => {
  const { user } = useAuth();
  const userId = user?.id;
  
  const [currentCampaign, setCurrentCampaign] = useState<LegacyCampaign | null>(null);
  const [selectedCampaignForDeletion, setSelectedCampaignForDeletion] = useState<Campaign | null>(null);
  const [selectedCampaignForPublish, setSelectedCampaignForPublish] = useState<Campaign | null>(null);
  const [selectedCampaignForView, setSelectedCampaignForView] = useState<Campaign | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isResultsSheetOpen, setIsResultsSheetOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  // Fetch campaigns from Supabase
  const { data: campaigns = [], isLoading, error } = useCampaignsQuery();
  
  // Get mutations - agora sem passar userId pois useCreateCampaign não precisa mais
  const { 
    createCampaign, 
    publishCampaign,
    publishExistingCampaign,
    updateCampaign, 
    deleteCampaign, 
    duplicateCampaign,
    deactivateCampaign,
    reactivateCampaign 
  } = useCampaignMutations(userId);
  
  // Filter campaigns by status - incluindo processing_grace_period
  const activeCampaigns = campaigns.filter(campaign => campaign.status === 'active');
  const scheduledCampaigns = campaigns.filter(campaign => campaign.status === 'scheduled');
  const completedCampaigns = campaigns.filter(campaign => campaign.status === 'completed');
  const draftCampaigns = campaigns.filter(campaign => campaign.status === 'draft');
  const processingCampaigns = campaigns.filter(campaign => campaign.status === 'processing_grace_period');

  // Handle create/publish campaign
  const handleCreateCampaign = async (campaignData: CampaignFormData, action: 'draft' | 'publish' = 'draft') => {
    console.log('📥 useCampaigns: handleCreateCampaign called');
    console.log('📥 useCampaigns: user from context:', user);
    console.log('📥 useCampaigns: userId:', userId);
    
    // Validação extra: garantir que o usuário está autenticado
    if (!user || !userId) {
      console.error('❌ useCampaigns: User not authenticated');
      toast.error('Usuário não autenticado. Por favor, faça login novamente.');
      return;
    }
    
    try {
      if (action === 'publish') {
        await publishCampaign.mutateAsync(campaignData);
      } else {
        await createCampaign.mutateAsync(campaignData);
      }
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create/publish campaign:', error);
    }
  };
  
  // Handle edit campaign
  const handleEditCampaign = async (campaignData: CampaignFormData, action: 'draft' | 'publish' = 'draft') => {
    if (!currentCampaign) return;
    
    try {
      if (action === 'publish' && currentCampaign.status === 'draft') {
        // First update the campaign data, then publish
        await updateCampaign.mutateAsync({ 
          id: currentCampaign.id, 
          data: campaignData 
        });
        
        // Convert to Campaign type for publishing
        const campaignToPublish: Campaign = {
          id: currentCampaign.id,
          user_id: userId!,
          name: campaignData.name,
          type: campaignData.type,
          product: campaignData.product,
          value: campaignData.value,
          start_date: campaignData.start_date.toISOString().split('T')[0],
          end_date: campaignData.end_date.toISOString().split('T')[0],
          status: 'draft',
          description: campaignData.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        await publishExistingCampaign.mutateAsync(campaignToPublish);
      } else {
        await updateCampaign.mutateAsync({ 
          id: currentCampaign.id, 
          data: campaignData 
        });
      }
      
      setCurrentCampaign(null);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to update campaign:', error);
    }
  };
  
  // Handle publish existing campaign
  const handlePublishCampaign = async () => {
    if (!selectedCampaignForPublish) return;
    
    try {
      await publishExistingCampaign.mutateAsync(selectedCampaignForPublish);
      setSelectedCampaignForPublish(null);
      setIsPublishDialogOpen(false);
    } catch (error) {
      console.error('Failed to publish campaign:', error);
    }
  };
  
  // Handle delete campaign
  const handleDeleteCampaign = async (campaignId: string) => {
    console.log('📥 useCampaigns: handleDeleteCampaign chamado com ID:', campaignId);
    
    if (!campaignId) {
      console.error('❌ useCampaigns: ID da campanha não fornecido');
      return;
    }
    
    try {
      console.log('🚀 useCampaigns: Iniciando mutação de exclusão...');
      await deleteCampaign.mutateAsync(campaignId);
      console.log('✅ useCampaigns: Mutação de exclusão concluída');
    } catch (error) {
      console.error('❌ useCampaigns: Falha ao excluir campanha:', error);
      throw error;
    }
  };

  // Handle duplicate campaign
  const handleDuplicateCampaign = async (campaign: Campaign) => {
    try {
      await duplicateCampaign.mutateAsync(campaign);
    } catch (error) {
      console.error('Failed to duplicate campaign:', error);
    }
  };

  // Handle deactivate campaign (renamed from handleDeactivateCampaign)
  const handleFinalizeCampaign = async (campaign: Campaign) => {
    try {
      await deactivateCampaign.mutateAsync(campaign.id);
    } catch (error) {
      console.error('Failed to finalize campaign:', error);
    }
  };

  // Handle reactivate campaign
  const handleReactivateCampaign = async (campaign: Campaign) => {
    try {
      await reactivateCampaign.mutateAsync(campaign);
    } catch (error) {
      console.error('Failed to reactivate campaign:', error);
    }
  };
  
  // Open edit modal with selected campaign
  const openEditModal = (campaign: LegacyCampaign) => {
    setCurrentCampaign(campaign);
    setIsEditModalOpen(true);
  };
  
  // Open delete dialog with selected campaign
  const openDeleteDialog = (campaign: LegacyCampaign) => {
    setCurrentCampaign(campaign);
    setIsDeleteDialogOpen(true);
  };
  
  // Open publish dialog with selected campaign
  const openPublishDialog = (campaign: Campaign) => {
    setSelectedCampaignForPublish(campaign);
    setIsPublishDialogOpen(true);
  };
  
  // Open results sheet with selected campaign
  const openResultsSheet = (campaign: LegacyCampaign) => {
    setCurrentCampaign(campaign);
    setIsResultsSheetOpen(true);
  };

  // Open view modal with selected campaign
  const openViewModal = (campaign: Campaign) => {
    setSelectedCampaignForView(campaign);
    setIsViewModalOpen(true);
  };

  return {
    campaigns,
    activeCampaigns,
    scheduledCampaigns,
    completedCampaigns,
    draftCampaigns,
    processingCampaigns,
    currentCampaign,
    selectedCampaignForDeletion,
    selectedCampaignForPublish,
    selectedCampaignForView,
    isCreateModalOpen,
    isEditModalOpen,
    isDeleteDialogOpen,
    isPublishDialogOpen,
    isResultsSheetOpen,
    isViewModalOpen,
    isLoading,
    error,
    setIsCreateModalOpen,
    setIsEditModalOpen,
    setIsDeleteDialogOpen,
    setIsPublishDialogOpen,
    setIsResultsSheetOpen,
    setIsViewModalOpen,
    setSelectedCampaignForDeletion,
    handleCreateCampaign,
    handleEditCampaign,
    handleDeleteCampaign,
    handlePublishCampaign,
    handleDuplicateCampaign,
    handleFinalizeCampaign,
    handleReactivateCampaign,
    openEditModal,
    openDeleteDialog,
    openPublishDialog,
    openResultsSheet,
    openViewModal
  };
};
